import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Text } from '@figma/fpl-components';
import { color, spacer } from '@figma/fpl-tokens/tokens.stylex';
import * as stylex from '@stylexjs/stylex';
import { I18N_KEYS } from '@/language/keys';
import { ToolOutputPreview, type ToolOutputPreviewSelection } from './ToolOutputPreview';
import { ToolOutputThumbnail } from './ToolOutputThumbnail';
import { isPreviewableOutput } from './ToolOutputDownload';
import type { UploadedAsset } from '@/types/api/assets';

interface ToolExampleOutputsProps {
  title: string;
  examples: UploadedAsset[];
}

export function ToolExampleOutputs({ title, examples }: ToolExampleOutputsProps) {
  const { t } = useTranslation();
  const labels = I18N_KEYS.TOOL_VIEW;
  const [selectedExample, setSelectedExample] = useState<ToolOutputPreviewSelection | null>(null);
  const previewableExamples = examples.filter(isPreviewableOutput);
  const firstExample = previewableExamples[0];

  return (
    <>
      {firstExample && (
        <section aria-label={t(labels.EXAMPLE_OUTPUTS)} {...stylex.props(styles.examples)}>
          <div {...stylex.props(styles.examplesHeader)}>
            <Text.h2 variant="body-large-strong" color="default" {...stylex.props(styles.sectionTitle)}>
              {t(labels.EXAMPLE_OUTPUTS)}
            </Text.h2>
            <button
              type="button"
              onClick={() => setSelectedExample({ id: firstExample.id })}
              {...stylex.props(styles.showAll)}
            >
              <Text.span variant="body-medium" color="default">
                {t(labels.SHOW_ALL_EXAMPLES)}
              </Text.span>
            </button>
          </div>
          <div {...stylex.props(styles.examplesRow)}>
            {previewableExamples.map((example, index) => {
              const label = t(labels.OPEN_EXAMPLE, { number: index + 1 });
              return (
                <button
                  type="button"
                  key={example.id}
                  aria-label={label}
                  onClick={() => setSelectedExample({ id: example.id })}
                  {...stylex.props(styles.exampleButton)}
                >
                  <ToolOutputThumbnail asset={example} selected fit="contain" />
                </button>
              );
            })}
          </div>
        </section>
      )}
      {selectedExample && (
        <ToolOutputPreview
          selection={selectedExample}
          results={previewableExamples}
          workflowName={title}
          onSelect={setSelectedExample}
          onClose={() => setSelectedExample(null)}
        />
      )}
    </>
  );
}

const styles = stylex.create({
  examples: { flexShrink: 0, minWidth: 0, paddingInline: spacer.spacer32px, paddingBottom: spacer.spacer32px },
  examplesHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: spacer.spacer12px,
    marginBottom: spacer.spacer12px,
  },
  showAll: {
    borderWidth: 0,
    padding: 0,
    backgroundColor: 'transparent',
    cursor: 'pointer',
    color: color.text,
    textDecoration: 'underline',
    flexShrink: 0,
  },
  examplesRow: { display: 'flex', gap: 14, overflowX: 'auto', paddingBottom: 2 },
  exampleButton: {
    width: 190,
    height: 130,
    flexShrink: 0,
    borderWidth: 0,
    padding: 0,
    borderRadius: 5,
    overflow: 'hidden',
    cursor: 'pointer',
    backgroundColor: color.bgSecondary,
    outline: { default: 'none', ':focus-visible': `2px solid ${color.borderSelected}` },
    outlineOffset: { default: 0, ':focus-visible': -2 },
  },
  sectionTitle: { margin: 0 },
});
